// No unwanted movement: things that appear or disappear with a mode (editing, selecting,
// read-only) keep their place in the layout and only fade. Text that becomes editable stays
// the same size and in the same place; only a field background fades in behind it.
import React, { useEffect, useRef } from 'react';
import { Animated, Platform, StyleSheet, Text, TextInput, View, type KeyboardTypeOptions, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { radius, usePalette } from '../theme';

const DURATION = 180;

/**
 * Always takes its space; fades its children in or out. Hidden children ignore touches and
 * are hidden from screen readers. `dim` keeps them faintly visible instead (e.g. 0.35).
 */
export function Fade({ visible, children, style, dim = 0 }: { visible: boolean; children?: React.ReactNode; style?: StyleProp<ViewStyle>; dim?: number }) {
  const opacity = useRef(new Animated.Value(visible ? 1 : dim)).current;
  useEffect(() => {
    Animated.timing(opacity, { toValue: visible ? 1 : dim, duration: DURATION, useNativeDriver: Platform.OS !== 'web' }).start();
  }, [visible, dim, opacity]);
  const hidden = !visible && dim === 0;
  return (
    <Animated.View
      style={[style, { opacity, pointerEvents: visible ? 'auto' : 'none' }]}
      accessibilityElementsHidden={hidden}
      importantForAccessibility={hidden ? 'no-hide-descendants' : 'auto'}
      aria-hidden={hidden}>
      {children}
    </Animated.View>
  );
}

/** Fades in once when mounted (for overlays that appear, like the selection bar). */
export function FadeIn({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const opacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(opacity, { toValue: 1, duration: DURATION, useNativeDriver: Platform.OS !== 'web' }).start();
  }, [opacity]);
  return <Animated.View style={[style, { opacity }]}>{children}</Animated.View>;
}

/**
 * Two things sharing one spot: `a` sits in the layout, `b` lies on top of it (top-left) and
 * the two cross-fade. Size the spot for the bigger one (e.g. a fixed-width slot).
 */
export function Swap({ showB, a, b, style, bStyle }: { showB: boolean; a: React.ReactNode; b: React.ReactNode; style?: StyleProp<ViewStyle>; bStyle?: StyleProp<ViewStyle> }) {
  return (
    <View style={style}>
      <Fade visible={!showB}>{a}</Fade>
      <Fade visible={showB} style={[StyleSheet.absoluteFill, { alignItems: 'center', justifyContent: 'center' }, bStyle]}>{b}</Fade>
    </View>
  );
}

/**
 * Text that turns into a field in place. The text itself sizes the box in both modes, so
 * switching to editing changes nothing in the layout: the input lies exactly over the text,
 * with the same font, and a field background fades in around it (outside the box).
 * An empty value still takes one line, so typing the first letter does not move anything.
 */
export function EditableText({
  editing, value, onChangeText, style, placeholder, multiline = true, keyboardType, accessibilityLabel, fieldColor, inset = { x: 8, y: 4 }, onBlur,
}: {
  editing: boolean; value: string; onChangeText: (v: string) => void; style: StyleProp<TextStyle>; placeholder?: string; multiline?: boolean;
  keyboardType?: KeyboardTypeOptions; accessibilityLabel?: string; fieldColor?: string; inset?: { x: number; y: number }; onBlur?: () => void;
}) {
  const p = usePalette();
  const flat = StyleSheet.flatten(style) ?? {};
  const lineHeight = flat.lineHeight ?? Math.round((flat.fontSize ?? 16) * 1.35);
  const textStyle = [style, { lineHeight }];
  // A trailing newline adds a line to the input but not to Text: keep them the same height.
  const sizer = value ? (value.endsWith('\n') ? `${value} ` : value) : placeholder || ' ';
  return (
    <View>
      <Fade
        visible={editing}
        style={{ position: 'absolute', top: -inset.y, bottom: -inset.y, left: -inset.x, right: -inset.x, backgroundColor: fieldColor ?? p.card, borderRadius: radius.m }}
      />
      <Text style={[textStyle, { opacity: value && !editing ? 1 : 0 }]} aria-hidden={editing || !value}>{sizer}</Text>
      {editing ? (
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onBlur={onBlur}
          placeholder={placeholder}
          placeholderTextColor={p.secondaryText}
          multiline={multiline}
          scrollEnabled={false}
          keyboardType={keyboardType}
          accessibilityLabel={accessibilityLabel ?? placeholder}
          style={[textStyle, StyleSheet.absoluteFill, { padding: 0, margin: 0, borderWidth: 0, textAlignVertical: 'top', backgroundColor: 'transparent' }, Platform.OS === 'web' ? ({ resize: 'none', overflow: 'hidden' } as object) : null]}
        />
      ) : null}
    </View>
  );
}
